import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Icon } from '@iconify/react';
import showToast from '@/components/ui/Toast';
import { updateClientPassword } from '@/services/clientAuth';
import SEO from '@/components/common/SEO';

export const ClientChangePasswordPage: React.FC = () => {
  const navigate = useNavigate();

  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const [isSaving, setIsSaving] = useState(false);

  // Criteria validation
  const hasMinLength = newPassword.length >= 8;
  const hasUpperAndLower = /[a-z]/.test(newPassword) && /[A-Z]/.test(newPassword);
  const hasNumber = /[0-9]/.test(newPassword);
  const hasSpecialChar = /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(newPassword);

  const strengthScore = [hasMinLength, hasUpperAndLower, hasNumber, hasSpecialChar].filter(Boolean).length;

  const getStrengthLabel = () => {
    if (!newPassword) return 'Belum diisi';
    if (strengthScore <= 1) return 'Sangat Lemah';
    if (strengthScore === 2) return 'Lemah';
    if (strengthScore === 3) return 'Sedang';
    return 'Sangat Kuat';
  };

  const isFormValid =
    currentPassword.trim().length > 0 &&
    newPassword.trim().length >= 8 &&
    confirmPassword.trim().length > 0 &&
    newPassword === confirmPassword;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!currentPassword) {
      showToast.error('Masukkan password saat ini');
      return;
    }
    if (!hasMinLength) {
      showToast.error('Password baru minimal 8 karakter');
      return;
    }
    if (newPassword !== confirmPassword) {
      showToast.error('Konfirmasi password baru tidak cocok');
      return;
    }

    try {
      setIsSaving(true);
      await updateClientPassword({
        current_password: currentPassword,
        new_password: newPassword,
        confirm_new_password: confirmPassword,
      });
      showToast.success('Password berhasil diperbarui!');
      setTimeout(() => {
        navigate('/profile/account');
      }, 1000);
    } catch (err: any) {
      showToast.error(err?.response?.data?.message || 'Gagal mengubah password');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="bg-[#F8FAFC] min-h-screen text-[#0E1726] pb-24 select-none">
      <SEO title="Ganti Password | Sporto" description="Ganti password akun Sporto kamu" />

      {/* Desktop Header */}
      <div className="hidden md:flex items-center gap-3 max-w-md mx-auto pt-2 pb-1">
        <button
          type="button"
          onClick={() => navigate(-1)}
          className="w-9 h-9 rounded-full bg-white border border-[#E0E6ED] flex items-center justify-center text-[#0E1726] hover:bg-gray-50 transition-colors cursor-pointer shadow-2xs"
        >
          <Icon icon="ph:arrow-left-bold" className="w-5 h-5 text-[#0E1726]" />
        </button>
        <h1 className="text-lg md:text-xl font-bold text-[#0E1726]">Ganti Password</h1>
      </div>

      {/* Content Container */}
      <form onSubmit={handleSubmit} className="max-w-md mx-auto px-4 pt-4 space-y-4 animate-fade-in">
        {/* Card 1: Input Fields */}
        <div className="bg-white rounded-3xl p-5 border border-[#E0E6ED] shadow-2xs space-y-4">
          {/* Password Lama */}
          <div className="space-y-1.5">
            <label className="text-xs font-bold text-[#0E1726] block">Password lama</label>
            <div className="relative flex items-center">
              <Icon icon="ph:lock-simple-bold" className="w-4 h-4 text-gray-400 absolute left-3.5 pointer-events-none" />
              <input
                type={showCurrentPassword ? 'text' : 'password'}
                placeholder="Masukkan password saat ini"
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
                className="w-full h-11 pl-9 pr-10 rounded-2xl border border-[#E0E6ED] bg-white text-xs font-medium text-[#0E1726] placeholder:text-gray-400 focus:outline-none focus:border-[#FC480D] transition-colors"
              />
              <button
                type="button"
                onClick={() => setShowCurrentPassword((prev) => !prev)}
                className="absolute right-3.5 text-gray-400 hover:text-gray-600 cursor-pointer p-0.5"
              >
                <Icon icon={showCurrentPassword ? 'ph:eye-slash-bold' : 'ph:eye-bold'} className="w-4 h-4" />
              </button>
            </div>
          </div>

          {/* Password Baru */}
          <div className="space-y-1.5">
            <label className="text-xs font-bold text-[#0E1726] block">Password baru</label>
            <div className="relative flex items-center">
              <Icon icon="ph:lock-simple-bold" className="w-4 h-4 text-gray-400 absolute left-3.5 pointer-events-none" />
              <input
                type={showNewPassword ? 'text' : 'password'}
                placeholder="Minimal 8 karakter"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                className="w-full h-11 pl-9 pr-10 rounded-2xl border border-[#E0E6ED] bg-white text-xs font-medium text-[#0E1726] placeholder:text-gray-400 focus:outline-none focus:border-[#FC480D] transition-colors"
              />
              <button
                type="button"
                onClick={() => setShowNewPassword((prev) => !prev)}
                className="absolute right-3.5 text-gray-400 hover:text-gray-600 cursor-pointer p-0.5"
              >
                <Icon icon={showNewPassword ? 'ph:eye-slash-bold' : 'ph:eye-bold'} className="w-4 h-4" />
              </button>
            </div>
          </div>

          {/* Ulangi Password Baru */}
          <div className="space-y-1.5">
            <label className="text-xs font-bold text-[#0E1726] block">Ulangi password baru</label>
            <div className="relative flex items-center">
              <Icon icon="ph:lock-simple-bold" className="w-4 h-4 text-gray-400 absolute left-3.5 pointer-events-none" />
              <input
                type={showConfirmPassword ? 'text' : 'password'}
                placeholder="Ketik ulang password baru"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                className="w-full h-11 pl-9 pr-10 rounded-2xl border border-[#E0E6ED] bg-white text-xs font-medium text-[#0E1726] placeholder:text-gray-400 focus:outline-none focus:border-[#FC480D] transition-colors"
              />
              <button
                type="button"
                onClick={() => setShowConfirmPassword((prev) => !prev)}
                className="absolute right-3.5 text-gray-400 hover:text-gray-600 cursor-pointer p-0.5"
              >
                <Icon icon={showConfirmPassword ? 'ph:eye-slash-bold' : 'ph:eye-bold'} className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>

        {/* Card 2: Kekuatan Password */}
        <div className="bg-white rounded-3xl p-5 border border-[#E0E6ED] shadow-2xs space-y-3.5">
          <div className="flex items-center justify-between">
            <h3 className="text-xs font-bold text-[#0E1726]">Kekuatan password</h3>
            <span
              className={`text-xs font-bold ${
                !newPassword
                  ? 'text-gray-400'
                  : strengthScore >= 4
                  ? 'text-emerald-600'
                  : strengthScore >= 2
                  ? 'text-amber-600'
                  : 'text-rose-500'
              }`}
            >
              {getStrengthLabel()}
            </span>
          </div>

          {/* Segment Bars (4 segments) */}
          <div className="grid grid-cols-4 gap-1.5">
            {[1, 2, 3, 4].map((seg) => {
              const active = newPassword && strengthScore >= seg;
              let segColor = 'bg-gray-200';
              if (active) {
                if (strengthScore <= 1) segColor = 'bg-rose-500';
                else if (strengthScore === 2) segColor = 'bg-amber-400';
                else if (strengthScore === 3) segColor = 'bg-amber-500';
                else segColor = 'bg-emerald-500';
              }
              return (
                <div
                  key={seg}
                  className={`h-1.5 rounded-full transition-all duration-300 ${segColor}`}
                />
              );
            })}
          </div>

          {/* Rules Checklist */}
          <div className="space-y-2 pt-1">
            {[
              { label: 'Minimal 8 karakter', valid: hasMinLength },
              { label: 'Ada huruf besar dan kecil', valid: hasUpperAndLower },
              { label: 'Ada minimal 1 angka', valid: hasNumber },
              { label: 'Ada karakter spesial (!@#$)', valid: hasSpecialChar },
            ].map((rule) => (
              <div key={rule.label} className="flex items-center gap-2.5 text-xs">
                {rule.valid ? (
                  <Icon icon="ph:check-circle-fill" className="w-4 h-4 text-emerald-500 shrink-0" />
                ) : (
                  <Icon icon="ph:circle-bold" className="w-4 h-4 text-gray-300 shrink-0" />
                )}
                <span className={`font-medium ${rule.valid ? 'text-[#0E1726] font-semibold' : 'text-gray-400'}`}>
                  {rule.label}
                </span>
              </div>
            ))}
          </div>
        </div>

        {/* Card 3: Notice Alert */}
        <div className="bg-[#FFF4EE] border border-orange-100 rounded-2xl p-4 flex items-start gap-3 text-xs text-[#0E1726] font-medium leading-relaxed shadow-2xs">
          <Icon icon="ph:info-bold" className="w-5 h-5 text-[#FC480D] shrink-0 mt-0.5" />
          <span>
            Setelah password diganti, kamu akan keluar otomatis dari perangkat lain yang masih login.
          </span>
        </div>

        {/* Submit Button */}
        <div className="pt-2">
          <button
            type="submit"
            disabled={!isFormValid || isSaving}
            className={`w-full h-12 rounded-full font-bold text-xs flex items-center justify-center gap-2 transition-all shadow-sm ${
              isFormValid && !isSaving
                ? 'bg-[#FC480D] hover:bg-[#D83B08] text-white cursor-pointer active:scale-98 shadow-orange-200'
                : 'bg-gray-200 text-gray-400 cursor-not-allowed shadow-none'
            }`}
          >
            {isSaving ? (
              <>
                <Icon icon="ph:spinner-gap-bold" className="w-4.5 h-4.5 animate-spin" />
                <span>Menyimpan Password...</span>
              </>
            ) : (
              <>
                <Icon icon="ph:check-bold" className="w-4 h-4" />
                <span>Simpan Password Baru</span>
              </>
            )}
          </button>
        </div>
      </form>
    </div>
  );
};

export default ClientChangePasswordPage;
